import type {
  EstateBand,
  EstateClusterEdge,
  EstateEdge,
  EstateNode,
} from "./estateTypes";
import { touches } from "./estateViewport";

export type EstateRouteFocus = {
  active: boolean;
  edgeKeys: Set<string>;
};

export function edgeClass(
  edge: EstateEdge,
  contextPageId: string | null,
  routeFocus: EstateRouteFocus,
): string {
  const classes = ["signal-estate-link"];
  const routeFocused = routeFocus.active && routeFocus.edgeKeys.has(edge.key);
  if (edge.link.agentBroken) {
    classes.push("is-broken");
  }
  if (routeFocused) {
    classes.push("is-route-focused");
  } else if (routeFocus.active) {
    classes.push("is-route-muted");
  }
  if (contextPageId && !routeFocused) {
    classes.push(touches(edge, contextPageId) ? "is-related" : "is-dimmed");
  }
  return classes.join(" ");
}

export function clusterEdgeClass(
  edge: EstateClusterEdge,
  activeCluster: string | null,
  routeFocusActive: boolean,
): string {
  const classes = ["signal-cluster-link"];
  if (edge.brokenCount > 0) {
    classes.push("is-broken");
  }
  if (activeCluster) {
    classes.push(
      edge.from.key === activeCluster || edge.to.key === activeCluster
        ? "is-related"
        : "is-dimmed",
    );
  }
  if (routeFocusActive) {
    classes.push("is-route-muted");
  }
  return classes.join(" ");
}

export function nodeClass(
  node: EstateNode,
  state: {
    selected: boolean;
    hovered: boolean;
    contextId: string | null;
    connectedIds: Set<string>;
    activeCluster: string | null;
    routeFocusActive: boolean;
    focusedPageIds: Set<string>;
  },
): string {
  const classes = ["signal-estate-node", `is-${node.state}`];
  const routeFocused =
    state.routeFocusActive && state.focusedPageIds.has(node.page.id);
  if (node.important) {
    classes.push("is-important");
  }
  if (state.selected) {
    classes.push("is-selected");
  }
  if (state.hovered) {
    classes.push("is-hovered");
  }
  if (routeFocused) {
    classes.push("is-route-focused");
  } else if (state.routeFocusActive && !state.selected && !state.hovered) {
    classes.push("is-route-muted");
  }
  if (
    state.contextId &&
    state.connectedIds.has(node.page.id) &&
    !state.selected &&
    !state.hovered &&
    !routeFocused
  ) {
    classes.push("is-related");
  }
  if (
    state.contextId &&
    !state.connectedIds.has(node.page.id) &&
    !routeFocused
  ) {
    classes.push("is-dimmed");
  }
  if (
    state.activeCluster &&
    node.cluster !== state.activeCluster &&
    !state.selected &&
    !state.hovered &&
    !state.connectedIds.has(node.page.id) &&
    !routeFocused
  ) {
    classes.push("is-dimmed");
  }
  return classes.join(" ");
}

export function pageEdgePath(edge: EstateEdge): string {
  const start = cardAnchor(edge.from, edge.to);
  const end = cardAnchor(edge.to, edge.from);
  return curvedPath(start, end);
}

export function clusterEdgePath(edge: EstateClusterEdge): string {
  const start = bandAnchor(edge.from, edge.to);
  const end = bandAnchor(edge.to, edge.from);
  return curvedPath(start, end);
}

function cardAnchor(
  node: EstateNode,
  target: EstateNode,
): { x: number; y: number } {
  const deltaX = target.x - node.x;
  const deltaY = target.y - node.y;
  if (Math.abs(deltaY) > Math.abs(deltaX) * 0.72) {
    return {
      x: node.x,
      y: node.y + Math.sign(deltaY || 1) * (node.height / 2),
    };
  }
  return {
    x: node.x + Math.sign(deltaX || 1) * (node.width / 2),
    y: node.y,
  };
}

function bandAnchor(
  band: EstateBand,
  target: EstateBand,
): { x: number; y: number } {
  const center = { x: band.x + band.width / 2, y: band.y + band.height / 2 };
  const targetCenter = {
    x: target.x + target.width / 2,
    y: target.y + target.height / 2,
  };
  const deltaX = targetCenter.x - center.x;
  const deltaY = targetCenter.y - center.y;
  if (Math.abs(deltaY) > Math.abs(deltaX) * 0.72) {
    return {
      x: center.x,
      y: center.y + Math.sign(deltaY || 1) * (band.height / 2),
    };
  }
  return {
    x: center.x + Math.sign(deltaX || 1) * (band.width / 2),
    y: center.y,
  };
}

function curvedPath(
  start: { x: number; y: number },
  end: { x: number; y: number },
): string {
  const vertical = Math.abs(end.y - start.y) > Math.abs(end.x - start.x) * 0.72;
  if (vertical) {
    const midY = (start.y + end.y) / 2;
    return `M ${start.x} ${start.y} C ${start.x} ${midY}, ${end.x} ${midY}, ${end.x} ${end.y}`;
  }
  const midX = (start.x + end.x) / 2;
  return `M ${start.x} ${start.y} C ${midX} ${start.y}, ${midX} ${end.y}, ${end.x} ${end.y}`;
}
